import './assets/styles/main.scss';

import clickOutside from '@common/components/clickOutside.js';
import loadingButton from '@common/components/loadingButton.js';
import { setupHttp } from '@common/composables/useHttp.js';
import { useMixpanel } from '@common/composables/useMixpanel.js';
import { getMMSHeaders } from '@common/utils/headers.js';
import { shouldReportErrorToSentry } from '@common/utils/sentryUtils.js';
import messages from '@intlify/unplugin-vue-i18n/messages';
import routes from '@mms/routes';
import * as Sentry from '@sentry/vue';
import mixpanel from 'mixpanel-browser';
import { createApp, h, ref } from 'vue';
import { createI18n } from 'vue-i18n';
import { createRouter, createWebHistory } from 'vue-router';

import App from './App.vue';
import { useUserStore } from './composables/useUserStore';
import globalComponents from './globalComponents.js';
import { authGuard } from './guards/authGuard';
import { authorityGuard } from './guards/authorityGuard';
import { verfiedMobileGuard } from './guards/verfiedMobileGuard';
import mainStore from './store/mainStore.js';
import { MMSErrorHandler } from './utils/MMSErrorHandler.js';
import './bootstrap.js';

const router = createRouter({
  history: createWebHistory('/'),
  routes: routes,
  scrollBehavior(to, from) {
    if (to.path === from.path) {
      return { left: 0 };
    }
    return { left: 0, top: 0 };
  },
});

const i18n = createI18n({
  // this will be removed in vue-i18n v10
  allowComposition: true,
  locale: 'en',
  messages,
  missingWarn: false,
  silentFallbackWarn: true,
  silentTranslationWarn: true,
});

setupHttp({
  baseURL: mainStore.appBaseUrl,
  errorHandler: (error: unknown, navigator: unknown) => MMSErrorHandler(error, navigator),
  headers: getMMSHeaders,
  router,
});

const app = createApp({
  data: function () {
    return {};
  },
  render() {
    return h(App, {
      props: {},
    });
  },
  router,
  setup: () => {
    const store = ref(mainStore);

    app.provide('mainStore', store);

    return {
      mainStore: store,
    };
  },
});

Sentry.init({
  app,
  beforeSend(event, hint) {
    const error = hint?.originalException;
    if (error && !shouldReportErrorToSentry(error as Error)) {
      return null;
    }
    return event;
  },
  dsn: import.meta.env.MIX_SENTRY_DSN,
  environment: import.meta.env.APP_ENV,
  integrations: [
    Sentry.browserTracingIntegration({ router }),
    Sentry.replayIntegration(),
    Sentry.vueIntegration({
      tracingOptions: {
        hooks: ['mount', 'update', 'unmount'],
        trackComponents: true,
      },
    }),
  ],
  replaysOnErrorSampleRate: 0.1,
  replaysSessionSampleRate: 0.1,
  tracePropagationTargets: [/^\/mms_internal/],
  tracesSampleRate: 0.5,
});

app.use(router);
app.use(i18n);
// Defining Global Component
Object.entries(globalComponents).forEach(([name, component]) => {
  app.component(name, component);
});
app.directive('click-outside', clickOutside);
app.directive('loading', loadingButton);

mixpanel.init(import.meta.env.MIX_PANEL_TOKEN, {
  debug: import.meta.env.DEV,
});

router.beforeEach(async (to, from, next) => {
  mainStore.setFromTo(to, from);
  setPageTitle(to.meta.pageTitle as string);
  const store = useUserStore();
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  const user = store.user.value as any;

  if (store.isAuthenticated.value && !user?.id) {
    try {
      await store.setUserInfo();
    } catch {
      return {
        name: 'login',
      };
    }
  }

  const redirect =
    authGuard(to, from, store) || verfiedMobileGuard(to, store) || authorityGuard(to, store);

  if (redirect) {
    return next(redirect);
  } else {
    next();
  }
});

router.afterEach((to, from) => {
  mainStore.setFromTo(to, from);

  const isNewPageVisit = to.path !== from.path;

  if (isNewPageVisit) {
    const { mixpanelTrackEvent } = useMixpanel();

    const eventName = to.meta.mixpanelVisitPageEventName ?? 'MMS Visit Page';

    mixpanelTrackEvent(eventName);
  }
});
function setPageTitle(title?: string) {
  const pageTitle = title ? `${title} - Foodics` : 'Foodics';
  document.title = pageTitle;
}

app.mount('#app');
